import { useEffect, useState, useRef } from 'react';
import { connect } from "react-redux";
import PropTypes from 'prop-types';
import { Row, Col, Spin, DatePicker, Table, Select, Input, Button, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import moment from "moment";
import systemActions from '../../redux/systemInfos/actions';
import { selectDailyStatisticsOfDayFromState } from '../../redux/systemInfos/selectors';
import { DvIntlMessages } from '../../components/utility/dvGeneralUtilities';
import IntlMessages from '../../components/utility/intlMessages';

const { RangePicker } = DatePicker;

const { getDailyStatisticsForDay } = systemActions;

const INIT_PAGE = 1;
const DATE_FORMAT = 'YYYY-MM-DD';
const DEFAULT_RECORDS_PER_PAGE = 100;
const DEFAULT_PAGE_SIZE_OPTIONS = [20, 50, 100, 500];

const MAP_DROP_DOWN_OPTIONS = {
  uniqueLogins: "uniqueLogins",
  totalLogins: "totalLogins",
  minutesSpent: "minutesSpent",
  uniquePatientsAccessed: "uniquePatientsAccessed",
  patientsCreated: "patientsCreated",
  patientsDeleted: "patientsDeleted",
  contributions: "contributions",
  nonDicomCount: "nonDicomCount",
  nonDicomSize: "nonDicomSize",
  dicomStudiesCount: "dicomStudiesCount",
  dicomSeriesCount: "dicomSeriesCount",
  dicomSize: "dicomSize"
};

function DailyStatistics(props) {
  const [currentPage, setCurrentPage] = useState(INIT_PAGE);
  const [recordsPerPage, setRecordsPerPage] = useState(DEFAULT_RECORDS_PER_PAGE);
  const [startDate, setStartDate] = useState(moment().add(-8, 'd'));
  const [endDate, setEndDate] = useState(moment().add(-1, 'd'));
  const [data, setData] = useState([]);
  const [selectedStatistic, setSelectedStatistic] = useState(Object.keys(MAP_DROP_DOWN_OPTIONS)[0]);
  const searchInput = useRef(null);
  const [selectedOrderCategory, setSelectedOrderCategory] = useState(undefined);
  const [order, setOrder] = useState(undefined);

  useEffect(() => {
    getStatisticsForPeriod();
  
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  useEffect(() => {
    setData(props.filterDailyStatistics(selectedStatistic, selectedOrderCategory, order, startDate, endDate))
  
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.dailyStatistics, selectedStatistic, selectedOrderCategory, order, startDate, endDate]);

  const getStatisticsForPeriod = () => {
    if (startDate) {
      let currentDate = startDate.clone();
      while (currentDate.isSameOrBefore(endDate, 'd')) {
        if (!props.selectDailyStatisticsOfDayFromStateFn(currentDate)) {
          props.getDailyStatisticsForDay(currentDate.format('YYYY-MM-DD'));
        }
        currentDate = currentDate.add(1, 'd');
      }
    }
  }

  function onChangeDatepicker(values) {
    const tmp_startDate = values === null || values[0] === null ? null : values[0];
    const tmp_endDate = values === null || values[1] === null ? null : values[1];

    setStartDate(tmp_startDate);
    setEndDate(tmp_endDate);
  }

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div
        style={{
          padding: 8,
        }}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{
            marginBottom: 8,
            display: 'block',
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            <IntlMessages id="general.search" />
          </Button>
          <Button
            onClick={() => {clearFilters(); confirm()} }
            size="small"
            style={{
              width: 90,
            }}
          >
            <IntlMessages id="general.reset" />
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? '#1890ff' : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) => text,
  });

  function constructTableColumns() {
    let columns = [
      {
        key: "title",
        title: "",
        dataIndex: 'title',
        sorter: (a, b) => a.title.localeCompare(b.title),
        width: 150,
        fixed: 'left',
        ...getColumnSearchProps('title')
      },
      {
        key: "total",
        title: DvIntlMessages("general.total"),
        dataIndex: 'total',
        sorter: (a, b) => a.total - b.total,
        width: 100,
        fixed: 'left'
      },
    ];

    let currentDate = startDate.clone();
    while (currentDate.isSameOrBefore(endDate, 'd')) {
      columns.push({
        key: currentDate.format(DATE_FORMAT),
        title: currentDate.format('dddd').substring(0,3) + ' ' + currentDate.format(DATE_FORMAT),
        dataIndex: currentDate.format(DATE_FORMAT),
        width: 150,
        sorter: true,
        render: (text, record) => {
          let valueToRender = text === null || text === undefined ? '-' : text;
          return {
            props: {
              style: { lineHeight: '20px', paddingTop: '0px', paddingBottom: '0px' }
            },
            children: (
              <span>{valueToRender}</span>
            )
          };
        }
      })

      currentDate = currentDate.add(1, 'd');
    }
    return columns;
  }

  function updateTableData(pagination, filters, sorter) {
    const { current, pageSize } = pagination;
    const { columnKey, order } = sorter;
    
    setSelectedOrderCategory(columnKey);
    setOrder(order);
    setCurrentPage(current);
    setRecordsPerPage(pageSize);
  }

  const columns = constructTableColumns()

  function handleExport() {
    let csv = "", csvContent = "";
    let filename = `${selectedStatistic}_${moment().format('dddd').substring(0, 3) + ' ' + moment().format(DATE_FORMAT)}`;
    const dataKeys = columns.map(e => e.key);
    let keysAmount = dataKeys.length

    for (let row = 0; row < data.length; row++) {
      let keysCounter = 0

      for (const key of dataKeys) {
        csv += data[row][key] == null || data[row][key] === undefined ? "-" : data[row][key];
        csv += (keysCounter + 1 < keysAmount ? ',' : '\r\n')
        keysCounter++;
      }

      keysCounter = 0
    }

    csvContent += columns.map(e => e.title).join(",") + '\r\n';
    csvContent += csv;

    let textEncoder = new TextEncoder('utf-8');
    let csvContentEncoded = textEncoder.encode([csvContent]);
    let blob = new Blob([csvContentEncoded], { type: 'text/csv;charset=utf-8;' });
    if (navigator.msSaveBlob) { // IE 10+
      navigator.msSaveBlob(blob, filename);
    } else {
      let link = document.createElement("a");
      if (link.download !== undefined) { // feature detection
        // Browsers that support HTML5 download attribute
        let url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  }

  const tableColumns = columns.map(item => ({ ...item, ellipsis: true }));
  const paginationConfig = {
    showSizeChanger: true,
    defaultPageSize: recordsPerPage,
    pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
    current: currentPage
  };
  return (
    <Spin spinning={props.loading > 0}>
      <Row gutter={[16, 16]} style={{ marginBottom: "25px" }}>
        <Col>
          <RangePicker
            allowEmpty={[false, false]}
            onChange={onChangeDatepicker}
            defaultValue={[startDate, endDate]}
          />
        </Col>
        <Col>
          <Select style={{ width: 230 }} onChange={setSelectedStatistic}
            defaultValue={selectedStatistic} allowClear={false}
            placeholder={DvIntlMessages("general.clinic")}>
            {Object.entries(MAP_DROP_DOWN_OPTIONS).map(([key, value]) => {
              return (
                <Select.Option value={key} key={key}>
                  <IntlMessages id={`dailyStatistics.filter.${value}`} />
                </Select.Option>
              )
            })}
          </Select>
        </Col>
        <Col>
            <Button onClick={handleExport} type="primary">
              <IntlMessages id="general.exportToCsv" />
            </Button>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <Table
            bordered={true}
            showHeader
            onChange={updateTableData}
            pagination={paginationConfig}
            columns={tableColumns}
            dataSource={data}
            size='small'
            scroll={{ x: 1500 }}
            onRow={(record, rowIndex) => {
                return {
                  onClick: event => { props.handleRowOnClick(record) },
                };
              }}
          />

        </Col>
      </Row>
    </Spin>
  )
}

DailyStatistics.propTypes = {
  selectDailyStatisticsOfDayFromStateFn: PropTypes.func.isRequired,
  getDailyStatisticsForDay: PropTypes.func.isRequired,
  dailyStatistics: PropTypes.array.isRequired,
  filterDailyStatistics: PropTypes.func.isRequired,
  handleRowOnClick: PropTypes.func.isRequired
};

DailyStatistics.defaultProps = {
}

const mapActionsToProps = {
  getDailyStatisticsForDay
};

function mapStateToProps(state) {
  return {
    selectDailyStatisticsOfDayFromStateFn: (dayAsMoment) => selectDailyStatisticsOfDayFromState(state, dayAsMoment),
    dailyStatistics: state.SystemInfos.get('dailyStatistics'),
    loading: state.SystemInfos.get('loading'),
  };
}

const connectedPage = connect(mapStateToProps, mapActionsToProps)(DailyStatistics);
export default connectedPage;